var _Combobox_indicator, _Combobox_wasOpenOnEnterDown;
import { __classPrivateFieldGet, __classPrivateFieldSet, __decorate } from "tslib";
import { ResourceType } from '@genesislcap/foundation-comms';
import { Combobox as FASTComboBox } from '@microsoft/fast-components';
import { attr, observable, Observable } from '@microsoft/fast-element';
import { ComboboxAutocomplete } from '@microsoft/fast-foundation';
import debounce from 'lodash.debounce';
import { OptionsDatasource } from '../options-datasource';
import { logger, wasClickInsideElement, wasClickOutsideElement } from '../utils';
import { dataserverCriteriaBuilder, requestServerCriteriaBuilder } from '../utils';
import { foundationComboboxStyles as styles } from './combobox.styles';
import { foundationComboboxTemplate as template } from './combobox.template';
const DEBOUNCE_TIME = 500;
/**
 * @tagname %%prefix%%-combobox
 */
export class Combobox extends FASTComboBox {
    constructor() {
        super(...arguments);
        _Combobox_indicator.set(this, void 0);
        _Combobox_wasOpenOnEnterDown.set(this, false);
        this.async = false;
        this.debounce = DEBOUNCE_TIME;
        /** Controls whether to perform a case-sensitive search in the list of options */
        this.caseSensitive = false;
        /** Controls whether to suppress opening the dropdown when the input field is clicked */
        this.ignoreInputClick = false;
    }
    debounceChanged(oldValue, newValue) {
        this.debouncedInputHandler = debounce(this.asyncSetFilter, newValue);
    }
    get value() {
        Observable.track(this, 'value');
        return this._ourValue;
    }
    set value(next) {
        var _a, _b, _c;
        const prev = `${this._ourValue}`;
        if (this.$fastController.isConnected && this.options) {
            const searchFunc = this.caseSensitive
                ? (el) => el.text === next
                : (el) => el.text.toLowerCase() === next.toLowerCase();
            const selectedIndex = this.options ? this.options.findIndex(searchFunc) : -1;
            const prevSelectedValue = (_a = this.options[this.selectedIndex]) === null || _a === void 0 ? void 0 : _a.text;
            const nextSelectedValue = (_b = this.options[selectedIndex]) === null || _b === void 0 ? void 0 : _b.text;
            this.selectedIndex =
                prevSelectedValue !== nextSelectedValue ? selectedIndex : this.selectedIndex;
            next = ((_c = this.firstSelectedOption) === null || _c === void 0 ? void 0 : _c.text) || next;
        }
        if (prev !== next) {
            this._ourValue = next;
            super.valueChanged(prev, next);
            Observable.notify(this, 'value');
        }
    }
    filterOptions() {
        if (!this.autocomplete || this.autocomplete === ComboboxAutocomplete.none) {
            this['filter'] = '';
        }
        const innerFilter = this['filter'];
        const [filter, searchFunc] = this.caseSensitive
            ? [innerFilter, (el) => el.text.startsWith(filter)]
            : [
                innerFilter.toLowerCase(),
                (el) => el.text.toLowerCase().startsWith(filter),
            ];
        this.filteredOptions = this._options.filter(searchFunc);
        if (!this['isAutocompleteList'])
            return;
        if (!this.filteredOptions.length && !filter)
            this.filteredOptions = this._options;
        this._options.forEach((o) => (o.hidden = !this.filteredOptions.includes(o)));
    }
    clickHandler(e) {
        if (this.disabled)
            return;
        // click outside, always close
        if (wasClickOutsideElement(e, this)) {
            this.open = false;
            return;
        }
        // always open/close on arrow box
        if (wasClickInsideElement(e, this.indicator)) {
            this.open = !this.open;
            if (this.open)
                this.control.focus();
            return;
        }
        // don't open when clicked in input
        if (this.ignoreInputClick && wasClickInsideElement(e, this.control))
            return;
        return super.clickHandler(e);
    }
    keydownHandler(e) {
        const key = e.key;
        if (e.ctrlKey || e.shiftKey)
            return true;
        switch (key) {
            case 'Enter':
                __classPrivateFieldSet(this, _Combobox_wasOpenOnEnterDown, this.open, "f");
                // don't swallow keys if the combobox isn't open
                if (!this.open)
                    return true;
                break;
            case 'Escape':
                // if value is different from current value, restore current value and leave
                if (this.control.value !== this.currentValue) {
                    this['filter'] = '';
                    this.filterOptions();
                    this.control.value = this.currentValue;
                    this.selectItemByValue(this.currentValue);
                    this.open = false;
                    return;
                }
                // don't swallow keys if the combobox isn't open
                if (!this.open)
                    return true;
                break;
        }
        const result = super.keydownHandler(e);
        if (key !== 'Enter')
            return result; // for Enter key, don't propagate keypress when selecting
    }
    keyupHandler(e) {
        const key = e.key;
        if (e.ctrlKey || e.shiftKey)
            return true;
        switch (key) {
            case 'Enter':
                return !__classPrivateFieldGet(this, _Combobox_wasOpenOnEnterDown, "f");
            case 'ArrowLeft':
            case 'ArrowRight':
            case 'Home':
            case 'End':
                // base class clears the selected value for this, which is silly
                return;
            case 'Backspace':
            case 'Delete':
                const result = super.keyupHandler(e);
                this.selectItemByValue(this.control.value);
                return result;
        }
        return super.keyupHandler(e);
    }
    /**
     * Gets the HTML element for the indicator button
     *
     * @protected
     */
    get indicator() {
        if (!__classPrivateFieldGet(this, _Combobox_indicator, "f"))
            __classPrivateFieldSet(this, _Combobox_indicator, this.shadowRoot.querySelector('[part="indicator"]'), "f");
        return __classPrivateFieldGet(this, _Combobox_indicator, "f");
    }
    /**
     * Selects an item in the dropdown based on its text value
     *
     * @param value - The text value of the item to be selected
     * @protected
     */
    selectItemByValue(value) {
        var _a, _b, _c;
        this.selectedIndex = (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.findIndex((el) => el.text === value)) !== null && _b !== void 0 ? _b : -1;
        (_c = this.options) === null || _c === void 0 ? void 0 : _c.forEach((o, i) => (o.selected = i === this.selectedIndex));
    }
    inputHandler(e) {
        if (this.async) {
            this.debouncedInputHandler();
        }
        else {
            super.inputHandler(e);
        }
    }
    asyncSetFilter() {
        const datasourceNode = this.slottedDatasource
            .assignedNodes()
            .filter((x) => x instanceof OptionsDatasource);
        if (datasourceNode.length > 0) {
            const resourceType = datasourceNode[0].datasource.resourceType;
            if (resourceType === ResourceType.DATASERVER) {
                dataserverCriteriaBuilder(datasourceNode[0], this.control.value);
            }
            if (resourceType === ResourceType.REQUEST_SERVER) {
                requestServerCriteriaBuilder(datasourceNode[0], this.control.value);
            }
        }
        else {
            logger.warn('Options-datasource is not connected. Please add a datasource to use this functionality.');
        }
    }
}
_Combobox_indicator = new WeakMap(), _Combobox_wasOpenOnEnterDown = new WeakMap();
__decorate([
    observable
], Combobox.prototype, "slottedDatasource", void 0);
__decorate([
    attr({ mode: 'boolean' })
], Combobox.prototype, "async", void 0);
__decorate([
    attr
], Combobox.prototype, "debounce", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'case-sensitive' })
], Combobox.prototype, "caseSensitive", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'ignore-input-click' })
], Combobox.prototype, "ignoreInputClick", void 0);
// explicitly declaring for extensibility
export const foundationComboboxShadowOptions = {
    delegatesFocus: true,
    mode: 'open',
};
export const defaultComboboxConfig = {
    indicator: `
        <svg
            class="combo-box-indicator"
            part="combo-box-indicator"
            viewBox="0 0 12 7"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M11.85.65c.2.2.2.5 0 .7L6.4 6.84a.55.55 0 01-.78 0L.14 1.35a.5.5 0 11.71-.7L6 5.8 11.15.65c.2-.2.5-.2.7 0z"
            />
        </svg>
    `,
};
/**
 * The Foundation Combo-box
 *
 * @public
 * @remarks
 * HTML Element: \<foundation-combo-box\>
 */
export const foundationCombobox = Combobox.compose(Object.assign({ baseName: 'combobox', template,
    styles, shadowOptions: foundationComboboxShadowOptions }, defaultComboboxConfig));
